import { Record } from 'immutable';
import { pickBy, isEmpty } from 'lodash';
import config from 'commons-vehicle/config/index.js';
import VIDEO_TYPES from 'commons-vehicle/constants/videoTypes.js';

const imagesServer = config.source.image;
const imagesBasePath = '/images';
const imageInventoryPath = '/inventory';
const imagesBasePathNewCar = '/images/newcar';

function buildImageUrls(imagePath) {
    return {
        url: `${imagesServer}${imagePath || ''}`,
        url360x270: `${imagesServer}/ir/w360h270${imagePath || ''}`,
        url360x240: `${imagesServer}/ir/w360h197c${imagePath || ''}`,
        url440: `${imagesServer}/ir/w440${imagePath || ''}`,
    };
}

function buildInventoryBasePath() {
    return `${imagesBasePath}${imageInventoryPath}`;
}

const MultiMediaRecordV25 = Record({
    mainPicture: '',
    mainPictureCompleteUrl: '',
    pictures: [],
    picturesWithCompleteUrl: [],
    exterior: {
        side: '',
        rear: '',
        front: '',
        colorCode: '',
    },
    interior: {
        dashboard: '',
        rear: '',
        front: '',
        colorCode: '',
    },
    catalog: {
        side: {
            url: '',
            url360x270: '',
            url360x240: '',
            url440: '',
        },
    },
    video: {
        videoFr: '',
        videoEn: '',
        video: '',
        videoTypeIdFr: VIDEO_TYPES.YOUTUBE,
        videoTypeIdEn: VIDEO_TYPES.YOUTUBE,
        videoTypeId: VIDEO_TYPES.YOUTUBE,
    },
    audio: {
        audioFr: '',
        audioEn: '',
        audio: '',
    },
});

class InventoryMultimediaModelV25 extends MultiMediaRecordV25 {
    constructor(obj) {
        const args = pickBy(obj);
        const newArgs = {};

        if (args) {
            if (args.pictures && args.pictures.length) {
                newArgs.pictures = args.pictures.map((picture) => {
                    return `${imagesServer}${buildInventoryBasePath()}${picture}`;
                });
            }

            if (args.mainPicture) {
                const imageBasePath = args.mainPicture.match(/^ca\/.*/)
                    ? `${imagesBasePathNewCar}/`
                    : buildInventoryBasePath();
                const imagePath = `${imageBasePath}${args.mainPicture?.url || args.mainPicture}`;

                newArgs.mainPicture = buildImageUrls(imagePath);
            }

            // With v2.5 API exterior obj to get new inventory vehicle side images that appear on VLP
            if (args?.exterior?.side) {
                const imagePath = args.exterior.side;
                const imageUrl = `${imagesBasePathNewCar}/${imagePath}`;
                newArgs.exterior = {
                    side: buildImageUrls(imageUrl),
                };
            }
        }

        super({ ...args, ...newArgs });
    }

    getMainImage() {
        return this.mainPicture && this.mainPicture.url ? this.mainPicture.url : '';
    }

    getMainImage360x270() {
        return this.mainPicture && this.mainPicture.url360x270 ? this.mainPicture.url360x270 : '';
    }

    getMainImage440() {
        return this.mainPicture && this.mainPicture.url440 ? this.mainPicture.url440 : '';
    }

    getCatalogSideImage() {
        return this.exterior?.side?.url || '';
    }

    getCatalogSideImage360x197() {
        return this.exterior?.side?.url360x240 || '';
    }

    hasCatalogImage() {
        return !isEmpty(this.catalog.side.url);
    }

    hasPictures() {
        return !isEmpty(this.pictures);
    }

    getPicture(index) {
        // eslint-disable-next-line no-restricted-globals
        if (!isFinite(index) || index < 0 || !this.pictures.length || index > this.pictures.length) {
            return '';
        }

        const picture = this.pictures[index];

        return picture || '';
    }

    hasVideo(lang = 'en') {
        return !!this.getVideo(lang);
    }

    getVideo(lang = 'en') {
        if (!isEmpty(lang) && lang.includes('fr')) return this.video?.videoFr || this.video?.video || '';
        return this.video?.videoEn || this.video?.video || '';
    }

    hasAudio() {
        return !!this?.audio?.audio;
    }

    getAudio() {
        return this.hasAudio() ? this.audio.audio : '';
    }
}

export { buildImageUrls, buildInventoryBasePath, imagesServer, imagesBasePath, imageInventoryPath };

export default InventoryMultimediaModelV25;
